import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api";
import "./upload.css";

// File validation constants
const MAX_FILE_SIZE_MB = 6;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
/** MIME types we accept; mobile often sends "" or application/octet-stream — see isAllowedImageFileAsync */
const ALLOWED_IMAGE_MIME = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/pjpeg",
  "image/x-png",
]);
const ALLOWED_EXTENSIONS_DISPLAY = "PNG, JPEG";

function extensionLooksLikePngOrJpeg(name) {
  if (!name || typeof name !== "string") return false;
  const ext = name.split(".").pop()?.toLowerCase();
  return ext === "png" || ext === "jpg" || ext === "jpeg" || ext === "jpe";
}

/**
 * JPEG vs PNG for normalizing data URLs (mobile often leaves Blob.type empty so
 * readAsDataURL yields data:;base64,... which APIs reject).
 */
async function sniffImageKind(file) {
  const type = (file.type || "").trim().toLowerCase();
  if (type === "image/png" || type === "image/x-png") return "png";
  if (
    type === "image/jpeg" ||
    type === "image/jpg" ||
    type === "image/pjpeg"
  )
    return "jpeg";
  if (ALLOWED_IMAGE_MIME.has(type))
    return type.includes("png") ? "png" : "jpeg";
  if (
    !type ||
    type === "application/octet-stream" ||
    type === "binary/octet-stream"
  ) {
    const n = (file.name || "").toLowerCase();
    if (n.endsWith(".png")) return "png";
    if (
      extensionLooksLikePngOrJpeg(file.name) &&
      !n.endsWith(".png")
    )
      return "jpeg";
  }
  const buf = await file.slice(0, 12).arrayBuffer();
  const u = new Uint8Array(buf);
  if (u.length >= 3 && u[0] === 0xff && u[1] === 0xd8 && u[2] === 0xff)
    return "jpeg";
  if (
    u.length >= 8 &&
    u[0] === 0x89 &&
    u[1] === 0x50 &&
    u[2] === 0x4e &&
    u[3] === 0x47 &&
    u[4] === 0x0d &&
    u[5] === 0x0a &&
    u[6] === 0x1a &&
    u[7] === 0x0a
  )
    return "png";
  return null;
}

function normalizeImageDataUrl(dataUrl, kind) {
  if (typeof dataUrl !== "string" || !kind) return dataUrl;
  const mime = kind === "png" ? "image/png" : "image/jpeg";
  return dataUrl.replace(/^data:[^;,]*(;base64,)/i, `data:${mime}$1`);
}

const P = process.env.PUBLIC_URL || "";

const DOC_ICON_SRC = {
  aadhar: `${P}/aadhar.jpeg`,
  vehicle: `${P}/vehicle.jpeg`,
  numberplate: `${P}/numberplate.jpeg`,
  photo: `${P}/photo.jpeg`,
};

const DOC_ITEMS = [
  {
    key: "aadhaar_front",
    label: "Upload Aadhaar ( Front )",
    icon: "aadhar",
  },
  {
    key: "aadhaar_back",
    label: "Upload Aadhaar ( Back )",
    icon: "aadhar",
  },
  {
    key: "driving_license",
    label: "Upload Local ID / Passport",
    icon: "vehicle",
    required: true,
  },
  {
    key: "vehicle_plate_photo",
    label: "Upload Vehicle Number Plate",
    icon: "numberplate",
  },
  {
    key: "selfie",
    label: "Upload Your Selfie",
    icon: "photo",
  },
];

// Document key list for validation (extracted from DOC_ITEMS)
// eslint-disable-next-line no-unused-vars
const DOC_KEYS = DOC_ITEMS.map((item) => item.key);
const SUPPORTED_COUNTRIES = ["IN", "US", "KE"];

// =========================================================================
// COUNTRY CONFIGURATION (Registry Pattern)
// =========================================================================
// Centralized configuration for country-specific rules.
// This makes it easier to add new countries without modifying
// scattered if/else logic throughout the component.

const COUNTRY_CONFIG = {
  IN: {
    name: "India",
    code: "IN",
    phoneDigits: 10,
    phonePrefix: "+91",
    phonePlaceholder: "10 digits starting with 6-9",
    requiresAadhaar: true,
    requiresLocalId: false,
    documents: {
      required: ["aadhaar_front", "aadhaar_back", "vehicle_plate_photo", "selfie"],
      optional: ["driving_license"],
    },
    labels: {
      aadhaarInput: "12-Digit Aadhaar Card Number",
      localIdLabel: "Aadhaar",
    },
  },
  US: {
    name: "United States",
    code: "US",
    phoneDigits: 14, // +1 + 10 digits
    phonePrefix: "+1",
    phonePlaceholder: "10 digits, e.g., 2125551234",
    requiresAadhaar: false,
    requiresLocalId: false,
    documents: {
      required: ["vehicle_plate_photo", "selfie"],
      optional: ["driving_license"], // Local ID optional for US
    },
    labels: {
      aadhaarInput: null, // US doesn't use Aadhaar
      localIdLabel: "Local ID / Passport (Optional)",
    },
  },
  KE: {
    name: "Kenya",
    code: "KE",
    phoneDigits: 12, // +254 + 9 digits
    phonePrefix: "+254",
    phonePlaceholder: "9 digits starting with 7",
    requiresAadhaar: false,
    requiresLocalId: true,
    documents: {
      required: ["driving_license", "vehicle_plate_photo", "selfie"], // Local ID required for KE
      optional: [],
    },
    labels: {
      aadhaarInput: null, // No Aadhaar input for Kenya
      localIdLabel: "Local ID / Passport *",
      helpText: "Kenya riders don't need Aadhaar. Please upload your local ID in the documents section.",
    },
  },
};

// =========================================================================
// TODO: Error codes from backend
// =========================================================================
// For better error handling, backend should return structured error codes
// instead of natural language strings. Example:
// { error: "INVALID_PHONE", message: "...", country: "KE" }
// Then frontend can map error codes to user messages:
// const ERROR_MESSAGES = {
//   INVALID_PHONE: { IN: "...", KE: "...", US: "..." },
//   INVALID_VEHICLE: { IN: "...", KE: "...", US: "..." },
// };
// =========================================================================

const MSG_NETWORK = "Can't connect. Check your network and try again.";

// =========================================================================
// ERROR CODE MAPPING - Structured error handling
// =========================================================================
// Backend returns error codes, frontend maps them to user messages.
// This decouples frontend from backend string literals.

const ERROR_CODES = {
  // Phone errors
  INVALID_PHONE: {
    IN: "Enter a valid Indian phone number (10 digits starting with 6-9).",
    US: "Enter a valid US phone number (10 digits, e.g., 2125551234).",
    KE: "Enter a valid Kenya phone number (9 digits starting with 7).",
  },
  PHONE_ALREADY_EXISTS: {
    default: "This phone number is already registered. Try a different number or contact support."
  },
  
  // Aadhaar errors
  AADHAAR_REQUIRED: {
    default: "Aadhaar number is required for India."
  },
  AADHAAR_NOT_ALLOWED: {
    default: "Aadhaar number should not be provided for your country."
  },
  INVALID_AADHAAR: {
    default: "Enter a valid 12-digit Aadhaar number (cannot start with 0 or 1)."
  },
  
  // Vehicle errors
  INVALID_VEHICLE: {
    IN: "Enter a valid Indian vehicle number (example: MH12AB1234).",
    US: "Enter a valid US vehicle plate (1-10 letters/numbers).",
    KE: "Enter a valid Kenya vehicle plate (3-7 characters, e.g., KAB 123C).",
  },
  
  // Name errors
  INVALID_NAME: {
    default: "Name should be between 2 and 100 characters. Use letters, spaces, and basic punctuation."
  },
  
  // OTP errors
  INVALID_OTP: {
    default: "Invalid OTP. Try again."
  },
  OTP_EXPIRED: {
    default: "OTP expired. Request a new OTP and try again."
  },
  OTP_MAX_ATTEMPTS: {
    default: "Too many wrong OTP attempts. Please request a new OTP."
  },
  OTP_MAX_RESENDS: {
    default: "Resend limit reached. Please try again later."
  },
  OTP_COOLDOWN: {
    default: "Please wait before requesting a new OTP."
  },
  
  // Document errors
  DOCUMENT_REQUIRED: {
    IN: "Required: Aadhaar, vehicle plate, and selfie.",
    US: "Required: Vehicle plate and selfie.",
    KE: "Required: Local ID/Passport, vehicle plate, and selfie.",
  },
  DOCUMENT_TOO_LARGE: {
    default: "A file is too large. Max 6 MB each."
  },
  DOCUMENT_INVALID_FORMAT: {
    default: "Use PNG or JPEG for every document."
  },
  
  // Submission errors
  SUBMISSION_NOT_FOUND: {
    default: "Session expired. Go back and submit your details again."
  },
  SUBMISSION_COMPLETED: {
    default: "This KYC session is already completed. Start a new submission if needed."
  },
  SESSION_EXPIRED: {
    default: "Session expired. Go back and submit your details again."
  },
  INVALID_UPLOAD_TOKEN: {
    default: "Please verify OTP again before uploading documents."
  },
  
  // General
  UNSUPPORTED_COUNTRY: {
    default: "Country must be IN, US, or KE."
  },
  DATABASE_ERROR: {
    default: "Server issue. Please try again."
  },
};

/**
 * Get user-friendly error message from structured error response.
 * Falls back to parsing raw message if error code not found.
 */
function getErrorMessage(error, context, country = "IN") {
  // Check if response has structured error code
  const errorData = error?.response?.data;
  if (errorData?.error) {
    const code = errorData.error;
    const codeConfig = ERROR_CODES[code];
    
    if (codeConfig) {
      // Try country-specific message first
      if (codeConfig[country]) {
        return codeConfig[country];
      }
      // Fall back to default message
      if (codeConfig.default !== undefined) {
        return codeConfig.default;
      }
    }
    
    // Error code not found in mapping - log for developers
    console.warn(
      `[ERROR MAPPING] Unknown error code: ${code}. ` +
      `Message: ${errorData.message}. ` +
      `Context: ${context}, Country: ${country}. ` +
      `Add this code to ERROR_CODES in Upload.jsx`
    );
    // Return backend message to not leave user hanging
    return errorData.message || "Something went wrong. Please try again.";
  }
  
  // Legacy fallback - parsing raw messages
  console.warn(
    `[ERROR MAPPING] Falling back to legacy error parsing. ` +
    `Consider adding error code to backend response.`
  );
  return kycUserErrorMessage(error, context, country);
}

/** Extract `detail` or `message` from an API error body. */
function getApiErrorRaw(error) {
  const data = error?.response?.data;
  if (!data) return null;
  const d = data.detail ?? data.message;
  if (d == null) return null;
  if (typeof d === "string") return d.trim();
  if (Array.isArray(d)) {
    const parts = d
      .map((item) => {
        if (typeof item === "string") return item;
        if (item?.msg) return item.msg;
        if (item?.message) return item.message;
        return null;
      })
      .filter(Boolean);
    if (parts.length) return parts.join(" ");
  }
  if (typeof d === "object" && typeof d.message === "string") return d.message.trim();
  return null;
}

/** Short UI copy (matches toast tone) from axios errors or API strings. */
function kycUserErrorMessage(error, context, country = "IN") {
  if (!error?.response) return MSG_NETWORK;

  const raw = getApiErrorRaw(error);
  const lower = (raw || "").toLowerCase();

  if (
    lower.includes("network") ||
    lower.includes("econnrefused") ||
    lower.includes("timeout")
  ) {
    return MSG_NETWORK;
  }

  if (context === "otp" || context === "resend") {
    if (lower.includes("invalid otp")) return raw || "Invalid OTP. Try again.";
    if (lower.includes("expired"))
      return "OTP expired. Request a new OTP and try again.";
    if (lower.includes("maximum attempts exceeded"))
      return "Too many wrong OTP attempts. Please request a new OTP.";
    if (lower.includes("maximum resend limit"))
      return "Resend limit reached. Please try again later.";
    if (lower.includes("please wait") && lower.includes("seconds")) return raw;
    if (lower.includes("not found") || lower.includes("submission"))
      return "Session expired. Go back and submit your details again.";
    if (lower.includes("already submitted or verified"))
      return "This KYC session is already completed. Start a new submission if needed.";
    if (lower.includes("failed to send otp"))
      return "Couldn't send OTP right now. Please try again.";
    if (raw && raw.length <= 88) return raw;
    return context === "resend"
      ? "Couldn't resend code. Try again."
      : "Couldn't verify OTP. Try again.";
  }

  if (context === "upload") {
    if (lower.includes("not waiting for documents") || lower.includes("otp is not verified"))
      return "Please verify OTP first, then upload documents.";
    if (lower.includes("aadhaar front and back photos are required"))
      return "For India, both Aadhaar front and back photos are required.";
    if (lower.includes("aadhaar photos should not be provided"))
      return "For US submissions, don't upload Aadhaar photos.";
    if (lower.includes("image data is required"))
      return "All required document images must be selected.";
    if (lower.includes("size") || lower.includes("large") || error?.response?.status === 413)
      return "A file is too large. Max 6 MB each.";
    if (lower.includes("heic") || lower.includes("heif"))
      return "Use JPEG or PNG from your phone (not HEIC). Change camera format to 'Most compatible' or convert the file.";
    if (
      lower.includes("format") ||
      lower.includes("mime") ||
      lower.includes("invalid type") ||
      lower.includes("unsupported") ||
      lower.includes("not allowed") ||
      /\bonly\b.*\b(jpe?g|png)\b/.test(lower) ||
      /\b(jpe?g|png)\b.*\bonly\b/.test(lower)
    )
      return "Use PNG or JPEG for every document.";
    if (raw && raw.length <= 88) return raw;
    return "Couldn't submit documents. Try again.";
  }

  if (context === "submit") {
    if (lower.includes("unsupported country"))
      return "Country must be IN, US, or KE.";
    if (lower.includes("previous kyc was rejected") && lower.includes("please wait"))
      return raw;
    if (lower.includes("already submitted") || lower.includes("already exists"))
      return "This phone number is already registered. Try a different number or contact support.";
    if (lower.includes("kenya") && lower.includes("phone"))
      return "Enter a valid Kenya phone number (9 digits starting with 7, e.g. 0712345678).";
    if (lower.includes("invalid us phone") || lower.includes("invalid phone number"))
      return country === "KE"
        ? "Enter a valid Kenya phone number (9 digits starting with 7)."
        : country === "US" 
          ? "Enter a valid US phone number (10 digits, e.g. 2125551234)."
          : "Enter a valid Indian phone number (10 digits starting with 6-9).";
    if (lower.includes("aadhaar number is required for indian applicants"))
      return "Aadhaar number is required for India.";
    if (lower.includes("aadhaar number should not be provided"))
      return `${country === "KE" ? "Kenya" : country === "US" ? "United States" : country} applicants should not provide Aadhaar.`;
    if (lower.includes("invalid aadhaar number"))
      return "Enter a valid 12-digit Aadhaar number (cannot start with 0 or 1).";
    if (lower.includes("invalid vehicle") || lower.includes("invalid indian vehicle") || lower.includes("format:") || (lower.includes("kenya") && lower.includes("vehicle")))
      return country === "KE"
        ? "Enter a valid Kenya vehicle plate (3-7 characters, e.g. KAB 123C)."
        : country === "US"
          ? "Enter a valid US vehicle plate (1-10 letters/numbers)."
          : "Enter a valid Indian vehicle number (example: MH12AB1234).";
    if (lower.includes("vehicle plate must be"))
      return country === "KE"
        ? "Kenya vehicle plate must be 3-7 characters (e.g. KAB 123C)."
        : country === "US"
          ? "Enter a valid US vehicle plate (1-10 letters/numbers)."
          : "Enter a valid Indian vehicle number (example: MH12AB1234).";
    if (lower.includes("name must be at least") || lower.includes("name must be at most") || lower.includes("name must be"))
      return "Name should be between 2 and 100 characters.";
    if (lower.includes("name contains invalid characters") || lower.includes("name may only contain"))
      return "Name contains invalid characters. Use letters, spaces, and basic punctuation only.";
    if (lower.includes("database error"))
      return "Server issue while saving your details. Please try again.";
    if (lower.includes("join code") && lower.includes("invalid"))
      return "Invalid join code. Please check and try again.";
  }

  if (lower.includes("phone")) return "Check your phone number.";
  if (lower.includes("aadhaar")) return "Check your Aadhaar number.";
  if (lower.includes("vehicle")) return "Check your vehicle number.";
  if (lower.includes("already") || lower.includes("exists"))
    return "This number may already be registered.";
  if (raw && raw.length <= 88) return raw;
  return "Couldn't continue. Please try again.";
}

/** API returned success: false with a message string — keep short. */
function kycInlineApiMessage(message, context) {
  const t = (message || "").trim();
  if (!t) {
    return context === "submit"
      ? "Couldn't send your details. Try again."
      : context === "otp"
        ? "Couldn't verify OTP. Try again."
        : context === "upload"
          ? "Couldn't submit documents. Try again."
          : "Something went wrong. Try again.";
  }
  if (t.length <= 88) return t;
  return kycUserErrorMessage({ response: { data: { detail: t } } }, context);
}

const STEPPER_STEPS = [
  { id: 1, label: "Personal Details" },
  { id: 2, label: "OTP" },
  { id: 3, label: "Documents" },
  { id: 4, label: "Bank Details" },
];

/** Dark bars for completed steps and current step (matches OTP mock). */
function KycStepper({ activeStep }) {
  return (
    <nav className="kyc-stepper" aria-label="KYC progress">
      <div className="kyc-stepper__bars">
        {STEPPER_STEPS.map((s) => {
          const isFilled = s.id <= activeStep;
          return (
            <div
              key={s.id}
              className={`kyc-stepper__cell${isFilled ? " kyc-stepper__cell--filled" : ""}`}
            >
              <div
                className={`kyc-stepper__bar${isFilled ? " kyc-stepper__bar--filled" : ""}`}
              />
              <span className="kyc-stepper__label">{s.label}</span>
            </div>
          );
        })}
      </div>
    </nav>
  );
}

function KycDocIcon({ type }) {
  const src = DOC_ICON_SRC[type];
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      className="kyc-doc-card__img-icon"
      width={36}
      height={36}
      decoding="async"
      aria-hidden
    />
  );
}

function KycDocCheck() {
  return (
    <span className="kyc-doc-card__check" aria-hidden>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="12" fill="#22c55e" />
        <path
          d="M7 12l3 3 7-7"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

const OTP_LENGTH = 6;

function emptyOtpDigits() {
  return Array(OTP_LENGTH).fill("");
}

export default function Upload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const joinCode = searchParams.get("code") || "";

  // Form state
  const [formData, setFormData] = useState({
    country: "IN",
    phone_number: "",
    name: "",
    aadhaar_number: "",
    vehicle_number: "",
  });
  const [files, setFiles] = useState({});
  const [agreed, setAgreed] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false); // Separate state for form submission
  const [step, setStep] = useState("form"); // "form" | "otp" | "documents" | "payment" | "success"

  const [paymentDetails, setPaymentDetails] = useState({
    payment_method: "upi", // "upi" | "bank"
    upi_id: "",
    bank_account_number: "",
    bank_ifsc_code: "",
    bank_account_holder_name: "",
    bank_account_type: "savings",
  });
  
  // Session state with persistence
  const [submissionId, setSubmissionId] = useState(() => sessionStorage.getItem('kyc_submission_id') || null);
  const [submissionCountry, setSubmissionCountry] = useState(() => sessionStorage.getItem('kyc_submission_country') || "IN");
  const [uploadToken, setUploadToken] = useState(() => sessionStorage.getItem('kyc_upload_token') || null);
  
  // Persist state to sessionStorage on change
  const persistSubmissionId = (id) => {
    if (id) sessionStorage.setItem('kyc_submission_id', id);
    else sessionStorage.removeItem('kyc_submission_id');
    setSubmissionId(id);
  };
  const persistSubmissionCountry = (country) => {
    sessionStorage.setItem('kyc_submission_country', country);
    setSubmissionCountry(country);
  };
  const persistUploadToken = (token) => {
    if (token) sessionStorage.setItem('kyc_upload_token', token);
    else sessionStorage.removeItem('kyc_upload_token');
    setUploadToken(token);
  };
  const clearKycSession = () => {
    sessionStorage.removeItem('kyc_submission_id');
    sessionStorage.removeItem('kyc_submission_country');
    sessionStorage.removeItem('kyc_upload_token');
    setSubmissionId(null);
    setSubmissionCountry("IN");
    setUploadToken(null);
  };
  
  const [otpDigits, setOtpDigits] = useState(() => emptyOtpDigits());
  const otpInputRefs = useRef([]);

  useEffect(() => {
    if (step !== "otp") return;
    otpInputRefs.current = otpInputRefs.current.slice(0, OTP_LENGTH);
    const t = window.setTimeout(() => otpInputRefs.current[0]?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [step]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "country") {
      setFormData((prev) => ({
        ...prev,
        country: value,
        aadhaar_number: value === "US" ? "" : prev.aadhaar_number,
      }));
      return;
    }
    // Auto-capitalize vehicle number for better UX
    const processedValue = name === "vehicle_number" ? value.toUpperCase() : value;
    setFormData({ ...formData, [name]: processedValue });
  };

  const handleFile = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;

    const fieldName = input.name;

    const imageKind = await sniffImageKind(file);
    if (!imageKind) {
      toast.error("Use PNG or JPEG only.");
      input.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`Keep each file under ${MAX_FILE_SIZE_MB} MB. Please note: max file size is ${MAX_FILE_SIZE_MB}MB per file.`);
      input.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const raw = reader.result;
      const base64 =
        typeof raw === "string"
          ? normalizeImageDataUrl(raw, imageKind)
          : raw;
      setFiles((prev) => ({
        ...prev,
        [fieldName]: { file, base64 },
      }));
    };
    reader.readAsDataURL(file);
  };

  // Document requirements by country - use COUNTRY_CONFIG as single source of truth
  const countryDocConfig = COUNTRY_CONFIG[submissionCountry] || COUNTRY_CONFIG.IN;
  const requiredDocKeys = countryDocConfig.documents.required;
  const visibleDocItems = DOC_ITEMS.filter((item) => {
    // IN: show all items (aadhaar + driving_license + others)
    if (submissionCountry === "IN") return true;
    // US/KE: hide Aadhaar items
    return item.key !== "aadhaar_front" && item.key !== "aadhaar_back";
  });
  
  // For KE and US, show "Local ID / Passport" label
  // For KE: required (*), For US: optional
  const getDocLabel = (item) => {
    if (item.key === "driving_license" && submissionCountry !== "IN") {
      const isRequired = requiredDocKeys.includes("driving_license");
      return isRequired 
        ? "Upload Local ID / Passport *" 
        : "Upload Local ID / Passport (Optional)";
    }
    return item.label;
  };
  const allUploaded = requiredDocKeys.every((k) => files[k]);

  const allFieldsFilled =
    formData.country.trim() !== "" &&
    formData.phone_number.trim() !== "" &&
    formData.name.trim() !== "" &&
    (!COUNTRY_CONFIG[formData.country]?.requiresAadhaar || formData.aadhaar_number.trim() !== "") &&
    formData.vehicle_number.trim() !== "";

  const canSubmitForm = allFieldsFilled && agreed && !loading && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setLoading(true);

    const payload = {
      country: formData.country,
      phone_number: formData.phone_number,
      name: formData.name,
      aadhaar_number:
        formData.country !== "IN" ? null : formData.aadhaar_number.trim() || null,
      // Normalize vehicle number to uppercase for consistent validation
      vehicle_number: formData.vehicle_number.trim().toUpperCase(),
      join_code: joinCode ? joinCode : null,
    };

    console.log("[KYC] Submitting form:", payload);

    try {
      const response = await api.post("/kyc/submit", payload);

      console.log("[KYC] Submit response:", response.data);

      if (response.data && response.data.success) {
        const newSubmissionId = response.data.submission_id;
        const backendCountry = response.data.country;

        console.log("[KYC] Submission created:", newSubmissionId, "Country:", backendCountry);

        // Update state with submission ID and country (persisted to sessionStorage)
        persistSubmissionId(newSubmissionId);
        
        // Always update submission country based on backend response, with fallback to form country
        const finalCountry = SUPPORTED_COUNTRIES.includes(backendCountry) 
          ? backendCountry 
          : formData.country;
        setSubmissionCountry(finalCountry);
        persistSubmissionCountry(finalCountry);

        setOtpDigits(emptyOtpDigits());
        setStep("otp");
        toast.success(
          response.data.message || "OTP sent. Please verify to continue."
        );
      } else {
        // Handle unexpected response structure
        const message = response.data?.message || "Unexpected response from server";
        console.error("[KYC] Submit failed:", message);
        toast.error(kycInlineApiMessage(message, "submit"));
      }
    } catch (error) {
      console.error("[KYC] Submit error:", error);
      toast.error(getErrorMessage(error, "submit", submissionCountry));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const verifyOtp = async () => {
    setLoading(true);

    const otpCode = otpDigits.join("");
    console.log("[KYC] Verifying OTP for submission:", submissionId, "OTP:", otpCode);

    try {
      const response = await api.post("/kyc/verify-otp", {
        submission_id: submissionId,
        otp: otpCode,
      });

      console.log("[KYC] Verify OTP response:", response.data);

      if (response.data && response.data.success) {
        // Update submission country from backend (source of truth after validation)
        const backendCountry = response.data.country;
        if (backendCountry && SUPPORTED_COUNTRIES.includes(backendCountry)) {
          // If backend corrected the country, log warning but proceed
          if (backendCountry !== formData.country) {
            console.warn(`[KYC] Country corrected by backend: ${formData.country} -> ${backendCountry}`);
          }
          persistSubmissionCountry(backendCountry);
        } else {
          // Backend returned unexpected country - use form country as fallback
          console.error(`[KYC] Invalid country from backend: ${backendCountry}, using form country: ${formData.country}`);
        }
        
        // Store upload token for secure document upload (persisted to sessionStorage)
        if (response.data.upload_token) {
          console.log("[KYC] Storing upload token");
          persistUploadToken(response.data.upload_token);
        }
        
        console.log("[KYC] OTP verified, moving to documents step");
        setStep("documents");
        toast.success(
          response.data.message || "OTP verified. Please upload your documents."
        );
      } else {
        setOtpDigits(emptyOtpDigits());
        const message = response.data?.message || "Verification failed";
        toast.error(kycInlineApiMessage(message, "otp"));
      }
    } catch (error) {
      console.error("[KYC] OTP verification error:", error);
      setOtpDigits(emptyOtpDigits());
      toast.error(getErrorMessage(error, "otp", formData.country));
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setLoading(true);

    console.log("[KYC] Resending OTP for submission:", submissionId);

    try {
      const response = await api.post("/kyc/resend-otp", {
        submission_id: submissionId,
      });

      console.log("[KYC] Resend OTP response:", response.data);

      if (response.data && response.data.success) {
        toast.success("Code sent again.");
      } else {
        const message = response.data?.message || "Resend failed";
        toast.error(kycInlineApiMessage(message, "resend"));
      }
    } catch (error) {
      console.error("[KYC] Resend OTP error:", error);
      toast.error(getErrorMessage(error, "resend", formData.country));
    } finally {
      setLoading(false);
    }
  };

  const uploadDocuments = async (skipSuccess = false) => {
    setLoading(true);

    console.log("[KYC] Uploading documents for submission:", submissionId, "Country:", submissionCountry);

    const docFormData = new FormData();
    docFormData.append("submission_id", submissionId);
    
    // Add secure upload token (required for document upload)
    if (uploadToken) {
      docFormData.append("upload_token", uploadToken);
      console.log("[KYC] Using upload token for secure upload");
    } else {
      console.error("[KYC] ERROR: No upload token! Please verify OTP first.");
      toast.error("Session expired. Please verify OTP again.");
      setLoading(false);
      return;
    }

    // Use COUNTRY_CONFIG for Aadhaar check (SOLID - single source of truth)
    if (submissionCountry === "IN") {
      if (files.aadhaar_front?.file) docFormData.append("aadhaar_front", files.aadhaar_front.file);
      if (files.aadhaar_back?.file) docFormData.append("aadhaar_back", files.aadhaar_back.file);
    }
    
    // Add driving license (Local ID/Passport) if present
    if (files.driving_license?.file) docFormData.append("driving_license", files.driving_license.file);
    if (files.vehicle_plate_photo?.file) docFormData.append("vehicle_plate_photo", files.vehicle_plate_photo.file);
    if (files.selfie?.file) docFormData.append("selfie", files.selfie.file);

    console.log("[KYC] FormData prepared with files:", Object.keys(files));

    try {
      // Axios automatically sets 'multipart/form-data' boundary when passing FormData
      const response = await api.post("/kyc/upload-documents", docFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log("[KYC] Document upload response:", response.data);

      if (response.data && response.data.success) {
        console.log("[KYC] Documents uploaded successfully");
        if (skipSuccess) {
          setStep("payment");
        } else {
          setStep("success");
          clearKycSession();
        }
        toast.success(
          skipSuccess
            ? "Documents uploaded. Now enter your bank details."
            : response.data.message || "Documents uploaded. Your KYC is under review."
        );
      } else {
        const message = response.data?.message || "Upload failed";
        toast.error(kycInlineApiMessage(message, "upload"));
      }
    } catch (error) {
      console.error("[KYC] Document upload error:", error);
      toast.error(getErrorMessage(error, "upload", submissionCountry));
    } finally {
      setLoading(false);
    }
  };

  // Render OTP verification step
  if (step === "otp") {
    const otpCode = otpDigits.join("");

    return (
      <div className="ai-root">
        <div className="upload-page upload-page--flow upload-page--otp">
          <h1 className="kyc-otp-title">Please Enter OTP</h1>

          <p className="otp-hint">
            Sent to number ending{" "}
            <strong>{formData.phone_number.slice(-4) || "— — — —"}</strong>
          </p>

          <div className="otp-form otp-form--boxed">
            <div
              className="otp-digits"
              role="group"
              aria-label="Enter 6-digit OTP"
            >
              {Array.from({ length: OTP_LENGTH }, (_, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    otpInputRefs.current[i] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  aria-label={`Digit ${i + 1} of ${OTP_LENGTH}`}
                  maxLength={1}
                  className="otp-digit"
                  value={otpDigits[i]}
                  disabled={loading}
                  onChange={(e) => {
                    const d = e.target.value.replace(/\D/g, "").slice(-1);
                    setOtpDigits((prev) => {
                      const next = [...prev];
                      next[i] = d;
                      return next;
                    });
                    if (d && i < OTP_LENGTH - 1) {
                      otpInputRefs.current[i + 1]?.focus();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace") {
                      if (otpDigits[i]) {
                        setOtpDigits((prev) => {
                          const next = [...prev];
                          next[i] = "";
                          return next;
                        });
                      } else if (i > 0) {
                        otpInputRefs.current[i - 1]?.focus();
                        setOtpDigits((prev) => {
                          const next = [...prev];
                          next[i - 1] = "";
                          return next;
                        });
                      }
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    if (i !== 0) return;
                    e.preventDefault();
                    const paste = e.clipboardData
                      .getData("text")
                      .replace(/\D/g, "")
                      .slice(0, OTP_LENGTH);
                    setOtpDigits((prev) => {
                      const next = [...prev];
                      for (let k = 0; k < OTP_LENGTH; k++) {
                        next[k] = paste[k] ?? "";
                      }
                      return next;
                    });
                    const nextFocus = Math.min(
                      Math.max(paste.length - 1, 0),
                      OTP_LENGTH - 1
                    );
                    requestAnimationFrame(() => {
                      otpInputRefs.current[nextFocus]?.focus();
                    });
                  }}
                />
              ))}
            </div>

            <button
              type="button"
              className="submit-btn submit-btn--dark submit-btn--wide"
              onClick={verifyOtp}
              disabled={otpCode.length !== OTP_LENGTH || loading}
            >
              {loading ? "Verifying..." : "Submit and Next"}
            </button>

            <button
              type="button"
              className="resend-btn resend-btn--otp"
              onClick={resendOtp}
              disabled={loading}
            >
              Resend OTP
            </button>
          </div>

          <KycStepper activeStep={2} />
        </div>
      </div>
    );
  }

  if (step === "documents") {
    return (
      <div className="ai-root">
        <div className="upload-page upload-page--flow upload-page--documents">
          <h1 className="kyc-docs-title">Upload Documents</h1>
          <p className="kyc-docs-subtitle">
            {submissionCountry === "US"
              ? "US flow: Upload driving license, plate photo, and selfie."
              : "We never share personal details to anyone"}
          </p>
          <p className="sr-only">
            Upload clear photos for each item. Accepted formats:{" "}
            {ALLOWED_EXTENSIONS_DISPLAY}. Maximum {MAX_FILE_SIZE_MB} MB per
            file.
          </p>

          <div className="kyc-doc-list">
            {visibleDocItems.map((item) => (
              <label
                key={item.key}
                className={`kyc-doc-card${loading ? " kyc-doc-card--disabled" : ""}`}
              >
                <span className="kyc-doc-card__icon">
                  <KycDocIcon type={item.icon} />
                </span>
                <span className="kyc-doc-card__label">{getDocLabel(item)}</span>
                {files[item.key] ? (
                  <KycDocCheck />
                ) : (
                  <span className="kyc-doc-card__check-spacer" aria-hidden />
                )}
                <input
                  type="file"
                  name={item.key}
                  onChange={handleFile}
                  disabled={loading}
                  accept="image/png,image/jpeg,.png,.jpg,.jpeg,.jpe"
                />
              </label>
            ))}
          </div>

          <div className="bottom-actions bottom-actions--documents">
            <button
              type="button"
              className="submit-btn submit-btn--dark submit-btn--wide"
              onClick={async () => {
                // Upload documents now (burns the upload token), then proceed to payment step
                await uploadDocuments(/* skipSuccess= */ true);
              }}
              disabled={!allUploaded || loading}
            >
              {loading ? "Uploading..." : "Next: Bank Details"}
            </button>
            <KycStepper activeStep={3} />
          </div>
        </div>
      </div>
    );
  }

  // Bank / payment details step
  if (step === "payment") {
    const isUpi = paymentDetails.payment_method === "upi";
    const canSubmit = isUpi
      ? paymentDetails.upi_id.trim().length > 0
      : paymentDetails.bank_account_number.trim().length > 0 &&
        paymentDetails.bank_ifsc_code.trim().length > 0 &&
        paymentDetails.bank_account_holder_name.trim().length > 0;

    const handlePaymentChange = (e) => {
      const { name, value } = e.target;
      setPaymentDetails((p) => ({ ...p, [name]: value }));
    };

    const handleFinalSubmit = async () => {
      if (!canSubmit) return;
      setLoading(true);
      try {
        await api.post("/kyc/payment-details", {
          submission_id: submissionId,
          payment_method: paymentDetails.payment_method,
          upi_id: isUpi ? paymentDetails.upi_id.trim() : null,
          bank_account_number: !isUpi ? paymentDetails.bank_account_number.trim() : null,
          bank_ifsc_code: !isUpi ? paymentDetails.bank_ifsc_code.trim() : null,
          bank_account_holder_name: !isUpi ? paymentDetails.bank_account_holder_name.trim() : null,
          bank_account_type: !isUpi ? paymentDetails.bank_account_type : null,
        });
        clearKycSession();
        setStep("success");
        toast.success("KYC submitted successfully! Under review in 24 hours.");
      } catch (error) {
        console.error("[KYC] Payment details error:", error);
        toast.error(getErrorMessage(error, "upload", submissionCountry));
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="ai-root">
        <div className="upload-page upload-page--flow upload-page--payment">
          <h1 className="kyc-docs-title">Bank Details</h1>
          <p className="kyc-docs-subtitle">
            Enter your payment details to receive delivery earnings
          </p>

          <div className="kyc-payment-form">
            {/* Method toggle */}
            <div className="kyc-payment-toggle">
              <button
                type="button"
                className={`kyc-payment-toggle__btn${isUpi ? " kyc-payment-toggle__btn--active" : ""}`}
                onClick={() => setPaymentDetails((p) => ({ ...p, payment_method: "upi" }))}
              >
                UPI
              </button>
              <button
                type="button"
                className={`kyc-payment-toggle__btn${!isUpi ? " kyc-payment-toggle__btn--active" : ""}`}
                onClick={() => setPaymentDetails((p) => ({ ...p, payment_method: "bank" }))}
              >
                Bank Account
              </button>
            </div>

            {isUpi ? (
              <div className="kyc-payment-fields">
                <label className="kyc-field-label" htmlFor="upi_id">
                  UPI ID
                </label>
                <input
                  id="upi_id"
                  name="upi_id"
                  type="text"
                  className="kyc-field-input"
                  placeholder="yourname@upi"
                  value={paymentDetails.upi_id}
                  onChange={handlePaymentChange}
                  disabled={loading}
                />
              </div>
            ) : (
              <div className="kyc-payment-fields">
                <label className="kyc-field-label" htmlFor="bank_account_holder_name">
                  Account Holder Name
                </label>
                <input
                  id="bank_account_holder_name"
                  name="bank_account_holder_name"
                  type="text"
                  className="kyc-field-input"
                  placeholder="Full name as on bank account"
                  value={paymentDetails.bank_account_holder_name}
                  onChange={handlePaymentChange}
                  disabled={loading}
                />

                <label className="kyc-field-label" htmlFor="bank_account_number">
                  Account Number
                </label>
                <input
                  id="bank_account_number"
                  name="bank_account_number"
                  type="text"
                  inputMode="numeric"
                  className="kyc-field-input"
                  placeholder="Enter account number"
                  value={paymentDetails.bank_account_number}
                  onChange={handlePaymentChange}
                  disabled={loading}
                />

                <label className="kyc-field-label" htmlFor="bank_ifsc_code">
                  IFSC Code
                </label>
                <input
                  id="bank_ifsc_code"
                  name="bank_ifsc_code"
                  type="text"
                  className="kyc-field-input"
                  placeholder="e.g. SBIN0001234"
                  value={paymentDetails.bank_ifsc_code}
                  onChange={(e) =>
                    setPaymentDetails((p) => ({
                      ...p,
                      bank_ifsc_code: e.target.value.toUpperCase(),
                    }))
                  }
                  disabled={loading}
                />

                <label className="kyc-field-label" htmlFor="bank_account_type">
                  Account Type
                </label>
                <select
                  id="bank_account_type"
                  name="bank_account_type"
                  className="kyc-field-input"
                  value={paymentDetails.bank_account_type}
                  onChange={handlePaymentChange}
                  disabled={loading}
                >
                  <option value="savings">Savings</option>
                  <option value="current">Current</option>
                </select>
              </div>
            )}
          </div>

          <div className="bottom-actions bottom-actions--documents">
            <button
              type="button"
              className="submit-btn submit-btn--dark submit-btn--wide"
              onClick={handleFinalSubmit}
              disabled={!canSubmit || loading}
            >
              {loading ? "Submitting..." : "Submit KYC"}
            </button>
            <KycStepper activeStep={4} />
          </div>
        </div>
      </div>
    );
  }

  // Success — flex order: hand ~30vh + centered, copy stack, spacer, CTA to bottom (photo layout)
  if (step === "success") {
    const handSrc = `${process.env.PUBLIC_URL || ""}/hands_2.png`;
    return (
      <div className="ai-root ai-root--success">
        <div className="upload-page upload-page--success">
          <div className="kyc-success-frame">
            <div className="kyc-success-hand">
              <img
                src={handSrc}
                alt=""
                className="kyc-success-hand__img"
                decoding="async"
              />
            </div>

            <h1 className="kyc-success-title">
              <span className="kyc-success-title__line">Your Application</span>
              <span className="kyc-success-title__line">Submitted Successfully</span>
            </h1>

            <p className="kyc-success-subtitle">
              We will get back to you in 24 Hours
            </p>

            <div className="kyc-success-spacer" aria-hidden />

            <div className="kyc-success-actions">
              <button
                type="button"
                className="submit-btn submit-btn--dark submit-btn--wide kyc-success-btn"
                onClick={() => navigate("/")}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Personal details step — matches mobile mockup
  return (
    <div className="ai-root">
      <div className="upload-page upload-page--details">
        <h1 className="kyc-details-title">
          Please Fill
          <br />
          Your Details
        </h1>

        <div className="form-section kyc-details-form">
          <div className="input-grid">
            <div className="input-group">
              <label htmlFor="country" className="sr-only">
                Country
              </label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                disabled={loading || submitting}
              >
                <option value="IN">India (IN)</option>
                <option value="US">United States (US)</option>
                <option value="KE">Kenya (KE)</option>
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="phone_number" className="sr-only">
                Phone number
              </label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                placeholder="Phone Number"
                value={formData.phone_number}
                onChange={handleInputChange}
                disabled={loading || submitting}
                maxLength={formData.country === "KE" ? 12 : formData.country === "US" ? 14 : 10}
                autoComplete="tel"
              />
            </div>

            <div className="input-group">
              <label htmlFor="name" className="sr-only">
                Full name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter Full Name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={loading || submitting}
                autoComplete="name"
              />
            </div>

            {formData.country === "IN" && COUNTRY_CONFIG["IN"]?.labels.aadhaarInput && (
              <div className="input-group">
                <label htmlFor="aadhaar_number" className="sr-only">
                  Aadhaar number
                </label>
                <input
                  type="text"
                  id="aadhaar_number"
                  name="aadhaar_number"
                  placeholder={COUNTRY_CONFIG["IN"]?.labels.aadhaarInput}
                  value={formData.aadhaar_number}
                  onChange={handleInputChange}
                  disabled={loading || submitting}
                  maxLength={12}
                  inputMode="numeric"
                />
              </div>
            )}

            <div className="input-group">
              <label htmlFor="vehicle_number" className="sr-only">
                Vehicle number
              </label>
              <input
                type="text"
                id="vehicle_number"
                name="vehicle_number"
                placeholder="Vehicle Number"
                value={formData.vehicle_number}
                onChange={handleInputChange}
                disabled={loading || submitting}
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        <div className="bottom-actions bottom-actions--details">
          <label className="agree-row agree-row--details">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={loading || submitting}
            />
            <span className="agree-text">
              I hereby agree that the above document belongs to me and
              voluntarily give my consent to OZU to utilize it as my address
              proof for KYC on purpose only. I've read and agree with the Terms
              and Conditions and the Privacy Policy.
            </span>
          </label>

          <button
            type="button"
            className="submit-btn submit-btn--dark submit-btn--wide"
            onClick={submit}
            disabled={!canSubmitForm}
          >
            {submitting ? "Sending..." : loading ? "Processing..." : "Get OTP"}
          </button>

          <KycStepper activeStep={1} />
        </div>
      </div>
    </div>
  );
}

